import { useState } from "react";
import { toast } from "sonner";

import { autoCompleteFor, inputModeFor } from "@/lib/form-a11y";
import { supabase } from "@/integrations/supabase/client";

const inputClass =
  "mt-2 w-full rounded-lg border border-white/20 bg-white px-3 py-2 text-base text-foreground outline-none focus:ring-2 focus:ring-accent";

/** "Get In Touch!" form on the Contact page. Saves to contact_messages. */
export function ContactForm() {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("");

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const value = (key: string) => String(data.get(key) ?? "").trim();

    setSubmitting(true);
    const { error } = await supabase.from("contact_messages").insert({
      first_name: value("first_name"),
      last_name: value("last_name") || null,
      email: value("email"),
      subject: value("subject"),
      message: value("message"),
    });
    setSubmitting(false);

    if (error) {
      toast.error("We couldn't send your message. Please try again.");
      setStatus("We couldn't send your message. Please try again.");
      return;
    }
    toast.success("Thank you — your message is on its way.");
    setStatus("Thank you — your message is on its way.");
    form.reset();
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-xl">
      <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <legend className="font-sans font-semibold text-white">
          Name <span className="text-[#e8615b]">*</span>
        </legend>
        <div className="mt-2">
          <input
            id="first_name"
            name="first_name"
            required
            aria-label="First name"
            className={inputClass}
            autoComplete={autoCompleteFor("first_name")}
            inputMode={inputModeFor("first_name")}
          />
          <label htmlFor="first_name" className="mt-1 block font-sans text-sm text-white/80">
            First
          </label>
        </div>
        <div className="mt-2">
          <input
            id="last_name"
            name="last_name"
            aria-label="Last name"
            className={inputClass}
            autoComplete={autoCompleteFor("last_name")}
            inputMode={inputModeFor("last_name")}
          />
          <label htmlFor="last_name" className="mt-1 block font-sans text-sm text-white/80">
            Last
          </label>
        </div>
      </fieldset>

      <div className="mt-6">
        <label htmlFor="email" className="font-sans font-semibold text-white">
          Email <span className="text-[#e8615b]">*</span>
        </label>
        <input
          id="email"
          name="email"
          type="email"
          required
          className={inputClass}
          autoComplete={autoCompleteFor("email")}
          inputMode={inputModeFor("email")}
        />
      </div>

      <div className="mt-6">
        <label htmlFor="subject" className="font-sans font-semibold text-white">
          Subject <span className="text-[#e8615b]">*</span>
        </label>
        <input
          id="subject"
          name="subject"
          required
          className={inputClass}
          autoComplete={autoCompleteFor("subject")}
          inputMode={inputModeFor("subject")}
        />
      </div>

      <div className="mt-6">
        <label htmlFor="message" className="font-sans font-semibold text-white">
          Message <span className="text-[#e8615b]">*</span>
        </label>
        <textarea id="message" name="message" rows={5} required className={inputClass} />
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="mt-8 rounded-lg bg-accent px-8 py-3 font-sans font-semibold text-navy transition hover:brightness-110 disabled:opacity-60"
      >
        {submitting ? "Sending…" : "Submit"}
      </button>
      <p role="status" aria-live="polite" className="sr-only">
        {status}
      </p>
    </form>
  );
}
